import React from 'react';
import styled from 'styled-components';
import dt from '../../../../shared/theme';
import { recordStatisticsEvent } from '../../../../services/statistics-service';
import { QuestionMark, TitleWithInfo } from '../../../shared/fund/info-styles';
import propTypes from '../../common/prop-types';
import useFundStateStore from '../../common/fund-state-hook';
import { Container, SSeparator, Title } from './styles';
import Fee from './fee';
import PerformanceFeeMethodology from './performance-fee-methodology';

const DetailsContainer = styled.div`
  display: flex;
  flex-direction: ${props => (dt(props).theme.isMobileOrTablet ? 'column' : 'row')};

  ${props => dt(props).theme.isLarge && `
    flex-wrap: wrap;
  `};

  ${props => dt(props).theme.isXXLarge && `
    flex-wrap: nowrap;
  `};

  & > div {
    min-width: ${props => (dt(props).theme.isMobile ? '100%' : '33%')};
    display: flex;
    flex-direction: column;
    margin-bottom: 40px;

    > .title {
      font: ${props => dt(props).theme.fontWeightBold} 14px/120% ${props => dt(props).theme.fontFamilyBody};
      color: ${props => dt(props).theme.secondaryColor};
      letter-spacing: 0.1px;
      text-transform: uppercase;
      margin-bottom: 4px;
      display: flex;
      justify-content: flex-start;
      height: 16px;

      .question-mark {
        ${QuestionMark};
      }
    }

    > .help {
      ${TitleWithInfo};
    }

    > .fee {
      > * {
        font: ${props => dt(props).theme.fontWeightBold} 32px/100% ${props => dt(props).theme.fontFamilyBody};
        color: ${props => dt(props).theme.bodyColor};
        letter-spacing: 0.1px;
      }

      > .small {
        margin-left: 4px;
        font-weight: ${props => dt(props).theme.fontWeightBold};
        font-size: 19px;
      }

      > .date {
        font-size: 14px;
        line-height: 16px;
        font-weight: ${props => dt(props).theme.fontWeightRegular};
        display: ${props => (dt(props).theme.isMobile ? 'inline' : 'block')};
        margin-left: ${props => (dt(props).theme.isMobile ? 8 : 0)}px;
      }
    }
  }

  & > div:last-child {
    margin-bottom: 0;
  }

  ${props => dt(props).theme.isLarge && `
    & > div:nth-child(1) {
      min-width: 50%;
    }
    & > div:nth-child(2) {
      min-width: 50%;
    }
    & > div:nth-child(3) {
      width: 100%;
    }
  `};

  ${props => dt(props).theme.isXXLarge && `
    & > div:nth-child(1) {
      min-width: 25%;
    }
    & > div:nth-child(2) {
      min-width: 25%;
    }
    & > div:nth-child(3) {
      min-width: 25%;
    }
  `};
`;

const PERFORMANCE_FEE_HELP = [
  'The Actual Performance Fee shown here is the actual fee that was charged most recently by the fund in relation to past performance and is not an estimate of the future performance fee that may be charged.',
];

const MAX_PERFORMANCE_FEE_HELP = [
  'A performance fee is a payment made to the manager for generating positive returns over a specific period of time based on set criteria.',
  'The Performance Fee is shown is in the "Fees and Costs Summary" table within the PDS and is intended to indicate the potential on-going performance fee that could be charged.',
];

export default function PerformanceFees({ fund }) {
  const {
    id,
    performanceFee,
    performanceFeePds,
    performanceFeeMethodology,
    financialYearEndFeesDate,
    pdsFileUrl,
  } = fund;

  const set = useFundStateStore(state => state.set);

  const showTooltipModal = (title, helpContent) => event => {
    event.preventDefault();
    set(state => {
      state.showTooltip = true;
      state.tooltipTitle = title;
      state.tooltipContent = helpContent;
    });

    recordStatisticsEvent({
      event: 'fund_profile_tooltip_clicked',
      statisticable: 'fund',
      id,
      clicked_url: title.replace(/[\s,/]/g, '_'),
      page: window.location.pathname,
    });
  };

  return (
    <Container>
      <Title>Performance Fee Details</Title>
      <SSeparator />
      <DetailsContainer>
        <Fee
          title="actual performance fee"
          value={performanceFee}
          date={financialYearEndFeesDate}
          helpContent={PERFORMANCE_FEE_HELP}
          handleClick={showTooltipModal}
        />
        <Fee
          title="performance fee"
          value={performanceFeePds}
          helpContent={MAX_PERFORMANCE_FEE_HELP}
          handleClick={showTooltipModal}
        />
        <div>
          <div className="title">
            <span className="fee-title">Performance Fee Methodology</span>
          </div>
          <PerformanceFeeMethodology
            fundId={id}
            methodology={performanceFeeMethodology}
            pdsFileUrl={pdsFileUrl}
          />
        </div>
      </DetailsContainer>
    </Container>
  );
}

/* eslint-disable-next-line react/require-default-props */
PerformanceFees.propTypes = { fund: propTypes.fund };
